import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { Formik } from 'formik';
import { updateSessionId } from 'modules/events/constants/eventsUpdate';
import { Event } from 'modules/events/types/eventsTypes';
import { EventEditForm } from './EventEditForm';
import { validationSchema } from './EventEditSchema';

type EventEditProps = {
  initialValues: object;
  editedEventDetails?: Event;
  onEventUpdate: (event: Event, formValues: object) => void;
};

export const EventEdit: FC<EventEditProps> = ({
  initialValues,
  editedEventDetails,
  onEventUpdate,
}) => {
  const { t } = useTranslation();
  const sessionDetailsState = useState<any>();

  const submit = (values: any) => {
    const [sessionDetails] = sessionDetailsState;

    const getSessionDetails = () => {
      /*
        Session data can come from backend if session seleted (`sessionDetails` object)
        or from event object (`editedEventDetails`) if that's an edit instead of create.
      */
      const isUpdatingEvent =
        values.session.id === updateSessionId && editedEventDetails && !sessionDetails;

      if (isUpdatingEvent) {
        return editedEventDetails!;
      }

      const [eventData] = sessionDetails.data.included;

      return {
        externalId: sessionDetails.data.data.id,
        type: eventData.type,
        title: eventData.attributes.title,
        organiser: `${eventData.attributes.owner.attributes.first_name} ${eventData.attributes.owner.attributes.last_name}`,
      };
    };

    const { externalId, type, title, organiser } = getSessionDetails();

    onEventUpdate(
      {
        status: values.activeEvent ? 'ACTIVE' : 'DRAFT',
        externalId,
        image: values.logo,
        type,
        title,
        shortDescription: values.description, // missing
        description: values.description,
        category: values.category.name.toUpperCase().replace(/ /g, '_'),
        region: values.region.name,
        industry: values.industry.name,
        eventDate: values.dateTime,
        duration: values.duration,
        languages: values.language,
        organiser,
        thirdPartySource: false,
        segment: values.segment.name,
        productLine: values.productLine.name,
        marketingLabel: values.marketingLabel,
        location: {
          name: values.location.name,
        },
        documentList: values.documents,
      },
      values,
    );
  };

  return (
    <Formik
      onSubmit={submit}
      validationSchema={validationSchema}
      initialValues={initialValues}
      validateOnChange={false}
    >
      {formData => (
        <Box px={8} py={4}>
          <Box mb={2}>
            <Typography variant="h5">{t('Specify event properties:')}</Typography>
          </Box>
          <EventEditForm sessionDetailsState={sessionDetailsState} {...formData} />
        </Box>
      )}
    </Formik>
  );
};
